import { Ref, ref } from "vue";
import { useRouter } from "vue-router";
import { Service as ApiService } from "@/apps/credit-line/services/network.service";
import { getErrorMessage } from "@/apps/credit-line/helpers/backCodesStrings.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { IWithdrawalEstimate } from "@/apps/credit-line/models/IWithdrawalEstimate";
import { IWithdrawalSecure } from "@/apps/credit-line/models/IWithdrawalSecure";
import {
  useGeolocation,
  GeolocationErrorResponse,
  GeolocationSuccessResponse,
} from "@/apps/credit-line/composables/useGeolocationFromNative";
import { useStore } from "vuex";

const apiService = new ApiService();

type SecureWithdrawalParams = {
  withdrawing: number;
  withdrawalEstimation: IWithdrawalEstimate;
  onSuccess?: (response: IWithdrawalSecure) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (error: any) => void;
};

export const useSecureWithdrawal = ({
  bankTransfer,
  t,
}: {
  bankTransfer: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (...args: any[]) => string;
}): {
  secureWithdrawal: (SecureWithdrawalParams) => void;
  securingWithdrawal: Ref<boolean>;
  showAlert: Ref<boolean>;
  alertMessage: Ref<string>;
} => {
  const router = useRouter();
  const store = useStore();
  const showAlert = ref<boolean>(false);
  const alertMessage = ref<string>("");
  const securingWithdrawal = ref<boolean>(false);

  const secureWithdrawal = async (params: SecureWithdrawalParams) => {
    securingWithdrawal.value = true;
    let geolocation: GeolocationSuccessResponse | null = null;
    if (bankTransfer) {
      try {
        geolocation = await useGeolocation();
      } catch (error: unknown) {
        // We expect to only reach this point if Geolocation permissions are already granted.
        // With that in mind, this is a best effort process for now. Consider blocking the process if needed.
        const geoError = error as GeolocationErrorResponse;
        console.error(geoError.errorType, geoError.error);
      }
    }
    const data = {
      deviceTag: AuthenticationHelper.getDeviceTag(),
      amount: params.withdrawing,
      annualPercentageRate: params.withdrawalEstimation.annualPercentageRate,
      expiry: params.withdrawalEstimation.expiry,
      hashValue: params.withdrawalEstimation.hashValue,
      bankTransfer,
      geolocation: geolocation,
    };

    apiService
      .secureWithdrawal(data)
      .then((response) => {
        if (response.data.debitId) {
          showAlert.value = false;
          securingWithdrawal.value = false;
          if (params.onSuccess) {
            params.onSuccess(response.data);
          } else {
            void store.dispatch("creditLine/clearWithdrawalEstimate");
            store.commit("creditLine/SET_SELECTED_WITHDRAW_METHOD", null);
            void router.push({ name: "CreditLineWithdrawalRequest" });
          }
        }
      })
      .catch((error) => {
        showAlert.value = true;
        alertMessage.value = getErrorMessage(
          (error.response &&
            error.response.data &&
            error.response.data.errorCode) ||
            705,
          t
        );
        if (error.response && error.response.data) {
          ErrorHandler.logCreditlineErrorResponse(error.response.data);
        }
        if (params.onError) {
          params.onError(error);
        }
      })
      .finally(() => {
        securingWithdrawal.value = false;
      });
  };

  return {
    secureWithdrawal,
    securingWithdrawal,
    showAlert,
    alertMessage,
  };
};
